import React from "react";
import { Modal, Input, Button, DatePicker, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";

const { TextArea } = Input;

interface ReservationRequestModalProps {
  open: boolean;
  onClose: () => void;
  formData: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSave: () => void;
  handleUpdate: () => void;
  action: { type: string } | null;
  loading: boolean;
  isManagement?: boolean;
}

const ReservationRequestModal: React.FC<ReservationRequestModalProps> = ({
  open,
  onClose,
  formData,
  handleChange,
  handleSave,
  handleUpdate,
  action,
  loading,
  isManagement = false,
}) => {
  const isEdit = action?.type === "edit";
  const [editDate, setEditDate] = React.useState(false);

  // Al cerrar el modal, se reinicia el estado para que el campo de fecha se bloquee nuevamente.
  React.useEffect(() => {
    if (!open) {
      setEditDate(false);
    }
  }, [open]);

  // Maneja el cambio del DatePicker y simula un evento compatible con handleChange
  const handleDateChange = (date: Moment | null, dateString: string | string[]) => {
    const formattedDateString = Array.isArray(dateString) ? dateString[0] : dateString;
    const event = {
      target: {
        id: "appointment_datetime",
        value: formattedDateString,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    handleChange(event);
  };

  // Función para limpiar el campo y permitir la edición de la fecha
  const handleChangeDateClick = () => {
    setEditDate(true);

    // Limpia la fecha en formData
    const clearEvent = {
      target: {
        id: "appointment_datetime",
        value: "",
      },
    } as React.ChangeEvent<HTMLInputElement>;
    handleChange(clearEvent);
  };

  // Funciones para aceptar o rechazar la solicitud en modo gestión
  const handleAccept = () => {
    Modal.confirm({
      title: "¿Estás seguro de aceptar esta solicitud?",
      onOk() {
        formData.status_request = "APPROVED";
        handleUpdate();
      },
    });
  };

  const handleReject = () => {
    Modal.confirm({
      title: "¿Estás seguro de rechazar esta solicitud?",
      onOk() {
        formData.status_request = "CANCELLED";
        handleUpdate();
      },
    });
  };

  return (
    <Modal
      title={
        isManagement
          ? "Gestionar Solicitud de Reserva"
          : isEdit
          ? "Editar Solicitud de Reserva"
          : "Crear Solicitud de Reserva"
      }
      open={open}
      onCancel={!loading ? onClose : undefined}
      footer={
        isManagement ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tooltip
              placement="left"
              title="Utilice los botones a la derecha para aprobar o rechazar la solicitud según la disponibilidad."
            >
              <span>
                <InfoCircleOutlined style={{ fontSize: "16px", color: "#1890ff" }} />
              </span>
            </Tooltip>
            <div>
              <Button
                key="accept"
                type="primary"
                onClick={handleAccept}
                loading={loading}
                disabled={formData.status_request === "APPROVED" || formData.status_request === "CANCELLED"}
              >
                Aceptar
              </Button>
              <Button
                key="reject"
                danger
                onClick={handleReject}
                loading={loading}
                style={{ marginLeft: 8 }}
                disabled={formData.status_request === "CANCELLED"}
              >
                Rechazar
              </Button>
            </div>
          </div>
        ) : (
          [
            <Button key="cancel" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={isEdit ? handleUpdate : handleSave}
              loading={loading}
              disabled={loading}
            >
              {isEdit ? "Actualizar" : "Crear"}
            </Button>,
          ]
        )
      }
      maskClosable={!loading}
      closable={!loading}
    >
      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", marginBottom: "4px" }}>
          Número de Celular <span style={{ color: "red" }}>*</span>
        </label>
        <Input
          id="chat_uuid"
          placeholder="Número de Celular"
          value={formData.chat_uuid}
          onChange={handleChange}
          disabled={loading || isEdit || isManagement}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", marginBottom: "4px" }}>
          Fecha y Hora <span style={{ color: "red" }}>*</span>{" "}
          {isManagement && !editDate && (
            <Button type="link" onClick={handleChangeDateClick} disabled={loading}>
              Cambiar
            </Button>
          )}
        </label>
        <DatePicker
          showTime
          id="appointment_datetime"
          placeholder="Seleccione Fecha y Hora"
          style={{ width: "100%" }}
          onChange={handleDateChange}
          // Carga la fecha del formData si existe
          value={formData.appointment_datetime ? moment(formData.appointment_datetime) : null}
          disabled={loading || (isManagement && !editDate)}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", marginBottom: "4px" }}>
          Comentarios <span style={{ color: "red" }}>*</span>
        </label>
        <TextArea
          id="comments"
          placeholder="Comentarios"
          value={formData.comments}
          onChange={handleChange}
          disabled={loading || isManagement}
          rows={3}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", marginBottom: "4px" }}>
          Correo Electrónico <span style={{ color: "red" }}>*</span>
        </label>
        <Input
          id="email"
          placeholder="Correo Electrónico"
          value={formData.email}
          onChange={handleChange}
          disabled={loading || isManagement}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", marginBottom: "4px" }}>
          Servicio o Producto <span style={{ color: "red" }}>*</span>
        </label>
        <Input
          id="service_or_product"
          placeholder="Servicio o Producto"
          value={formData.service_or_product}
          onChange={handleChange}
          disabled={loading || isManagement}
        />
      </div>

      <div style={{ marginBottom: "10px" }}>
        <label style={{ display: "block", marginBottom: "4px" }}>
          Nombre de Usuario <span style={{ color: "red" }}>*</span>
        </label>
        <Input
          id="user_name"
          placeholder="Nombre de Usuario"
          value={formData.user_name}
          onChange={handleChange}
          disabled={loading || isManagement}
        />
      </div>
    </Modal>
  );
};

export default ReservationRequestModal;